import { useEffect, useMemo, useRef, useState } from 'react';
import { CAPABILITIES, capabilityById } from '../../data/heroCapabilities';
import { setCapability } from '../../hooks/heroCapabilityStore';
import CapabilityHead from './CapabilityHead';

/* How long each capability holds before the row advances. Also drives the fill
   animation on the active chip, so the two can never drift apart. */
const DWELL_MS = 4600;

export default function Hero() {
  const [activeId, setActiveId] = useState(CAPABILITIES[0].id);
  const [held, setHeld] = useState(false);
  /* bumped on every change so the fill animation restarts from empty */
  const [tick, setTick] = useState(0);
  const reduce = useRef(false);

  const cap = useMemo(() => capabilityById(activeId), [activeId]);

  useEffect(() => {
    reduce.current =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }, []);

  /* Tell the live simulation which capability to show. */
  useEffect(() => {
    setCapability(activeId);
  }, [activeId]);

  /* Advance on its own; hovering or focusing the row holds the current one so
     nothing moves out from under someone who's reading it. */
  useEffect(() => {
    if (held || reduce.current) return undefined;
    const id = setTimeout(() => {
      setActiveId((curr) => {
        const i = CAPABILITIES.findIndex((c) => c.id === curr);
        return CAPABILITIES[(i + 1) % CAPABILITIES.length].id;
      });
      setTick((t) => t + 1);
    }, DWELL_MS);
    return () => clearTimeout(id);
  }, [activeId, held, tick]);

  const pick = (id) => {
    setActiveId(id);
    setTick((t) => t + 1);
  };

  return (
    <>
<header className="hero">
  <div className="container-wide">
    <div className="hero-grid">
      <div className="hero-copy">
        <span className="hero-badge">
          <img className="mark" src="/assets/mark-teamgrid.svg" alt="" />
          Meet your org's brain — Pattern AI <span className="arr">›</span>
        </span>
        <h1>See how your company<br /><span className="dim">actually works</span></h1>
        <ul className="hero-checks">
          <li><svg aria-hidden="true" className="chk" viewBox="0 0 16 16"><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg><span><b>Install in minutes.</b> One agent, 10 or 10,000 computers.</span></li>
          <li><svg aria-hidden="true" className="chk" viewBox="0 0 16 16"><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg><span><b>See every pattern.</b> Productivity, attendance & comms — live.</span></li>
          <li><svg aria-hidden="true" className="chk" viewBox="0 0 16 16"><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg><span><b>Stay human.</b> No screenshots. No keystrokes. Ever.</span></li>
        </ul>
        <div className="hero-cta-row">
          <a className="btn-primary" href="/register">Get started. It's FREE!</a>
          <span className="hero-cta-note">7-day free trial.<br />No credit card.</span>
        </div>
        <p className="hero-eyebrow">100% VISIBILITY {'\u00a0'}•{'\u00a0'} <span className="def" title="Patterns, not people: TeamGrid measures the shape of work — hours, rhythms, hand-offs — never screenshots, keystrokes or private content.">0% SURVEILLANCE</span></p>
        <p style={{fontSize: '12.5px', color: 'var(--ink-faint)', margin: '-8px 0 14px'}}>Built for founders & team leads of 10–500-person companies.</p>
        <div
          className="chips"
          role="tablist"
          aria-label="What TeamGrid shows you"
          style={{ '--chip-ms': `${DWELL_MS}ms` }}
          onMouseEnter={() => setHeld(true)}
          onMouseLeave={() => setHeld(false)}
          onFocusCapture={() => setHeld(true)}
          onBlurCapture={() => setHeld(false)}
        >
          {CAPABILITIES.map((c) => {
            const on = c.id === activeId;
            return (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={on}
                aria-controls="live-demo"
                className={`chip${on ? ' active' : ''}`}
                onClick={() => pick(c.id)}
              >
                {/* remounts on every change so the fill restarts from empty */}
                {on && <span className="chip-fill" key={tick} aria-hidden="true" />}
                <span className="chip-lab">{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="hero-visual">
        <div className="mock" id="live-demo">
          <div className="mock-top">
            <span className="mock-ws"><span className="wsdot"></span> Arclight ▾</span>
            <span className="mock-search">⌕{'\u00a0'} Ask TeamGrid anything…</span><span style={{fontSize: '9px', fontWeight: '800', letterSpacing: '1.2px', color: '#83848c', border: '1px solid #ececf0', borderRadius: '999px', padding: '3px 9px', whiteSpace: 'nowrap'}}>LIVE PRODUCT SIMULATION</span>
            <span style={{width: '120px'}}></span>
          </div>
          <div className="mock-body2">
            <div className="agents-pane">
              <div className="ap-head">ONE LIGHTWEIGHT AGENT</div>
              <div className="ap-count"><span id="agentCount">10</span><span className="ap-unit">computers connected</span></div>
              <div className="ap-sub">works for 10 — or 10,000</div>
              <div className="ap-grid-label"><svg aria-hidden="true" viewBox="0 0 16 16"><path fill="none" stroke="#007A6E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>Agent installed on every computer</div>
              <div className="team-stack" id="teamStack">
                <div className="scan-bar" id="scanBar" aria-hidden="true"></div>
                <div className="fleet-tip" id="fleetTip" aria-hidden="true"></div>
              </div>
              <div className="ap-foot">5-minute install · patterns, not people<br />no screenshots · no keystrokes</div>
            </div>
            <div className="flow-pane" id="flowPane">
              <div className="brain-big" id="brainBig"><img src="/assets/mark-teamgrid.svg" alt="TeamGrid Pattern Intelligence" /></div>
            </div>
            <div className="feed-pane">
              <div className="fp-head"><img src="/assets/mark-teamgrid.svg" style={{width: '17px', height: '17px'}} alt="" /><span className="livedot"></span><span className="fp-cap">{cap.label}</span><span className="fp-live">LIVE</span><button className="fp-pause" id="feedPause">PAUSE</button></div>
              <p className="fp-blurb">{cap.blurb}</p>
              <CapabilityHead head={cap.head} />
              <div className="feed" id="feed"></div>
              <div className="fp-ask">⌕{'\u00a0'}<span className="fp-askpre">Ask TeamGrid:</span>{'\u00a0'}<span id="askType"></span><span className="caret"></span></div>
            </div>
            <div className="flow-layer" id="flowLayer">
              <svg aria-hidden="true" id="flowSvg" preserveAspectRatio="none">
                <path /><path /><path /><path /><path /><path /><path />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</header>
    </>
  );
}
